import { supabase } from "../config";
import { normalizeConfirmation, type PaystackReturnConfirmation } from "./confirmation-outcome";

// Paystack's standard callback adds these to the configured callback URL
// after a payment. We also detect them on the ChiDesk return path; the
// ChiDesk handler runs FIRST (it scopes by its own vt_chidesk_ref param)
// and strips these along with its own. So when this module sees them,
// they belong to the Vertu paid-booking flow.
const PAYSTACK_QUERY_KEYS = ["reference", "trxref"] as const;
const CHIDESK_GUARD_KEY = "vt_chidesk_ref";

// The answer's shape and how the widget reads it live in confirmation-outcome.ts
// (pure, pinned by e2e/confirmation-outcome-logic.spec.ts).
export type {
  PaystackReturnBooking,
  PaystackReturnConfirmation,
  PaystackReturnTicket,
} from "./confirmation-outcome";

/**
 * Read the Paystack return reference from the current URL, ignoring the
 * ChiDesk callback shape (which has its own resume modal).
 */
export function readPaystackReturnReference(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.has(CHIDESK_GUARD_KEY)) return null;
  for (const key of PAYSTACK_QUERY_KEYS) {
    const v = params.get(key);
    if (v && v.length > 0) return v;
  }
  return null;
}

/**
 * Remove the Paystack return params from the URL so a refresh doesn't
 * re-trigger the lookup. Uses replaceState so it doesn't add to history.
 */
export function stripPaystackReturnReference(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  for (const key of PAYSTACK_QUERY_KEYS) url.searchParams.delete(key);
  window.history.replaceState({}, "", url.toString());
}

/**
 * Looks up the confirmed bookings + customer fields for a Paystack
 * payment reference (get_booking_confirmation_by_payment_reference). ok:true
 * only when a booking this payment paid for is confirmed (20261001160010);
 * otherwise payment_received_not_confirmed, payment_refunded, or the answers
 * from before. Read through normalizeConfirmation: an ok:true that names no
 * booking is never taken as a confirmation.
 */
export async function fetchPaystackReturnConfirmation(
  reference: string,
): Promise<PaystackReturnConfirmation> {
  const { data, error } = await supabase.rpc(
    "get_booking_confirmation_by_payment_reference",
    { p_reference: reference },
  );
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return normalizeConfirmation(data);
}

